import React, { useCallback, useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

interface PageRow { page: string; hits: number; }
interface SourceRow { source: string; hits: number; uniqueVisitors: number; }
interface PageSourceRow { page: string; source: string; hits: number; }

interface StatsResponse {
  windowDays: number | null;
  includeSmoke: boolean;
  totalEvents: number;
  byPage: PageRow[];
  bySource: SourceRow[];
  byPageSource: PageSourceRow[];
  fetchedAt: string;
}

type Window = 'all' | '1' | '7' | '30';

const WINDOW_OPTIONS: { label: string; value: Window }[] = [
  { label: 'All time', value: 'all' },
  { label: 'Last 24 hours', value: '1' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
];

export const AdminPageViewStats: React.FC = () => {
  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [window, setWindow] = useState<Window>('all');
  const [includeSmoke, setIncludeSmoke] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const days = window === 'all' ? undefined : Number(window);
      const res = await adminService.getPageViewStats({ days, includeSmoke });
      setData(res);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [window, includeSmoke]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Study Page Clicks</h2>
        <select value={window} onChange={(e) => setWindow(e.target.value as Window)}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
          {WINDOW_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
          <input type="checkbox" checked={includeSmoke}
            onChange={(e) => setIncludeSmoke(e.target.checked)} />
          Include smoke tests
        </label>
        <button onClick={load} disabled={loading}
          style={{ padding: '6px 14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        {data && (
          <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 'auto' }}>
            Fetched {new Date(data.fetchedAt).toLocaleTimeString()} · {data.totalEvents} events
          </span>
        )}
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {data && (
        <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
          <Panel title="By page">
            <TableSimple headers={['Page', 'Hits']} rows={data.byPage.map((r) => [r.page, r.hits])}
              empty="No hits in this window." />
          </Panel>

          <Panel title="By source (UTM)">
            <TableSimple
              headers={['Source', 'Hits', 'Unique']}
              rows={data.bySource.map((r) => [r.source, r.hits, r.uniqueVisitors])}
              empty="No hits in this window."
            />
          </Panel>

          <Panel title="By page + source" style={{ gridColumn: '1 / -1' }}>
            <TableSimple
              headers={['Page', 'Source', 'Hits']}
              rows={data.byPageSource.map((r) => [r.page, r.source, r.hits])}
              empty="No hits in this window."
            />
          </Panel>
        </div>
      )}
    </div>
  );
};

const Panel: React.FC<{ title: string; children: React.ReactNode; style?: React.CSSProperties }> = ({ title, children, style }) => (
  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, ...style }}>
    <h3 style={{ margin: '0 0 12px 0', fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.03em', color: '#6b7280', fontWeight: 600 }}>
      {title}
    </h3>
    {children}
  </div>
);

const TableSimple: React.FC<{ headers: string[]; rows: (string | number)[][]; empty: string }> = ({ headers, rows, empty }) => {
  if (rows.length === 0) return <div style={{ color: '#6b7280', fontSize: 13 }}>{empty}</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={h} style={{ textAlign: i === headers.length - 1 || (i > 0 && typeof rows[0][i] === 'number') ? 'right' : 'left',
              padding: '6px 8px', borderBottom: '1px solid #e5e7eb', color: '#374151', fontWeight: 600 }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((v, j) => (
              <td key={j} style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6',
                textAlign: typeof v === 'number' ? 'right' : 'left',
                fontFamily: typeof v === 'number' ? 'ui-monospace, monospace' : undefined,
                color: '#111827' }}>
                {v}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default AdminPageViewStats;
